import { gql } from '@apollo/client/core'
import { query, mutate } from './client'

/**
 * Service for managing announcements
 * This service handles all API calls related to announcements
 */

const GET_ANNOUNCEMENTS = gql`
  query GetAnnouncements {
    getAnnouncements {
      content
      isDeleted
      timeCreated
      _id
    }
  }
`

const ADD_ANNOUNCEMENT = gql`
  mutation AddAnnouncement($announcement: String!) {
    addAnnouncement(announcement: $announcement) {
      content
      isDeleted
      timeCreated
      _id
    }
  }
`

const UPDATE_ANNOUNCEMENT = gql`
  mutation UpdateAnnouncement($id: MongoID!, $record: String!) {
    announcementUpdateOne(filter: {_id: $id}, record: {content: $record}) {
      record {
        _id
        content
        isDeleted
        timeCreated
      }
      recordId
    }
  }
`

const DELETE_ANNOUNCEMENT = gql`
  mutation DeleteAnnouncement($id: MongoID!) {
    announcementRemoveById(_id: $id){
      recordId
    }
  }
`

/**
 * Get all announcements
 * Maps _id to id and content to text for consistency across the application
 * @returns {Promise} - Returns a new response object with mapped data
 */
export const getAnnouncements = async () => {
  const response = await query(GET_ANNOUNCEMENTS)
  
  // Map GraphQL fields to application fields for consistency
  // Return a new object since response.data may be frozen/sealed
  if (response?.data?.getAnnouncements) {
    const announcements = response.data.getAnnouncements.map(announcement => ({
      ...announcement,
      id: announcement._id,
      text: announcement.content
    }))
    
    return {
      ...response,
      data: {
        ...response.data,
        announcements
      }
    }
  }
  
  return response
}

/**
 * Create a new announcement
 * Maps _id to id and content to text for consistency
 * @param {Object} announcementData - The announcement data
 * @param {string} announcementData.text - The announcement text
 * @returns {Promise} - Returns a new response object with mapped data
 */
export const createAnnouncement = async (announcementData) => {
  const response = await mutate(ADD_ANNOUNCEMENT, {
    announcement: announcementData.text
  })
  
  // Map GraphQL fields to application fields for consistency
  // Return a new object since response.data may be frozen/sealed
  if (response?.data?.addAnnouncement) {
    const announcement = {
      ...response.data.addAnnouncement,
      id: response.data.addAnnouncement._id,
      text: response.data.addAnnouncement.content
    }
    
    return {
      ...response,
      data: {
        ...response.data,
        announcement
      }
    }
  }
  
  return response
}

/**
 * Update an announcement
 * Maps GraphQL response fields to application fields for consistency
 * 
 * GraphQL Response Structure:
 * {
 *   data: {
 *     announcementUpdateOne: {
 *       record: { _id, content, isDeleted, timeCreated },
 *       recordId: "..."
 *     }
 *   }
 * }
 * 
 * @param {Object} announcementData - The announcement data
 * @param {string} announcementData.id - The announcement ID
 * @param {string} announcementData.content - The updated announcement text
 * @returns {Promise} - Returns a new response object with mapped data
 */
export const updateAnnouncement = async (announcementData) => {
  const response = await mutate(UPDATE_ANNOUNCEMENT, {
    id: announcementData.id,
    record: announcementData.content
  })
  
  // Map GraphQL fields to application fields for consistency
  // GraphQL returns the updated record nested inside announcementUpdateOne.record
  if (response?.data?.announcementUpdateOne?.record) {
    const rawRecord = response.data.announcementUpdateOne.record
    
    const announcement = {
      // Spread the full record from GraphQL response
      ...rawRecord,
      // Add normalized fields for application use
      id: rawRecord._id,
      text: rawRecord.content
    }
    
    return {
      ...response,
      data: {
        ...response.data,
        announcement
      }
    }
  }
  
  return response
}

/**
 * Delete an announcement
 * @param {string} announcementId - The announcement ID
 * @returns {Promise} - Returns the axios response
 */
export const deleteAnnouncement = async (announcementId) => {
   return mutate(DELETE_ANNOUNCEMENT, {
    id: announcementId
  })
}